import {
  Badge,
  Button,
  CalendarSmall,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Selector,
  TextInput,
} from "@somethings-ui/web";
import { Calendar, CalendarClock, Info, Plus } from "lucide-react";
import { cn } from "@somethings-ui/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import {
  canceledLine,
  defaultTimeOffDates,
  displayEnd,
  draftInstants,
  findOverlap,
  formatWindow,
  isoFromPicker,
  overlapMessage,
  pickerDate,
  previewCopy,
  timeChoices,
  timeOffBadge,
  zoneAbbreviation,
  zoneLabel,
  type TimeOffDraft,
  type TimeOffEntry,
  type TimeOffType,
} from "@/lib/schedule/schedule-display";
import { scrollSelectedTime, TIME_MENU_CLASS } from "./time-select";

const timeOffSchema = z.object({
  type: z.enum(["pto", "emergency", "other"]),
  note: z.string().max(50),
  startDate: z.string().min(1, "Pick a start date"),
  endDate: z.string().min(1, "Pick an end date"),
  startTime: z.string().min(1),
  endTime: z.string().min(1),
});

type TimeOffFormValues = z.infer<typeof timeOffSchema>;

const TYPE_OPTIONS = [
  { value: "pto", title: "PTO", description: "Planned time away" },
  {
    value: "emergency",
    title: "Emergency time off",
    description: "Unexpected time away",
  },
  { value: "other", title: "Other", description: "Anything else" },
] as const;

const FIELD_CLASS =
  "flex h-14 w-full items-center justify-between rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 px-5 font-body text-body-2 text-text-primary outline-none";

const BADGE_CLASS: Record<TimeOffType, string> = {
  pto: "bg-brand-periwinkle/40 text-brand-periwinkle500",
  emergency: "bg-brand-apricot/25 text-brand-apricot",
  other: "bg-surfaces-z4 text-text-secondary",
};

const START_TIMES = timeChoices(false);
const END_TIMES = timeChoices(true).filter(
  (choice) => choice.value !== "00:00",
);

interface TimeOffCardProps {
  timezone: string;
  current: TimeOffEntry[];
  upcoming: TimeOffEntry[];
  past: TimeOffEntry[];
  disabled?: boolean;
  pending?: boolean;
  onSave: (draft: TimeOffDraft, editingId?: string) => Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onCancelCurrent: (id: string) => void | Promise<void>;
}

export function TimeOffCard({
  timezone,
  current,
  upcoming,
  past,
  disabled,
  pending,
  onSave,
  onDelete,
  onCancelCurrent,
}: TimeOffCardProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TimeOffEntry | null>(null);
  const [tab, setTab] = useState(current.length > 0 ? "current" : "upcoming");
  const [confirming, setConfirming] = useState<{
    id: string;
    mode: "current" | "upcoming";
  } | null>(null);
  const [confirmingPending, setConfirmingPending] = useState(false);

  const tabs = [
    ...(current.length > 0
      ? [{ value: "current", label: `Current (${current.length})` }]
      : []),
    { value: "upcoming", label: `Upcoming (${upcoming.length})` },
    { value: "past", label: `Past (${past.length})` },
  ];
  const activeTab = tabs.some((item) => item.value === tab) ? tab : "upcoming";
  const rows =
    activeTab === "current" ? current : activeTab === "past" ? past : upcoming;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-header-5 text-text-primary">
            Time off
          </h2>
          <p className="mt-1 font-body text-body-2 text-text-secondary">
            Your working hours will be paused on days you schedule time off.
          </p>
        </div>
        {!disabled && !open && (
          <Button
            fullWidth={false}
            variant="primary"
            size="sm"
            className="shrink-0"
            leadingIcon={<Plus className="size-4" />}
            onPress={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            Add time off
          </Button>
        )}
      </div>
      {open && (
        <TimeOffForm
          key={editing?.id ?? "new"}
          timezone={timezone}
          editing={editing}
          entries={[...current, ...upcoming]}
          pending={pending}
          onCancel={() => setOpen(false)}
          onSave={async (draft) => {
            await onSave(draft, editing?.id);
            setOpen(false);
            setEditing(null);
            const window = draftInstants(draft, timezone);
            if (!window) return;
            const now = DateTime.now();
            setTab(
              window.start <= now && now < window.end ? "current" : "upcoming",
            );
          }}
        />
      )}

      <div
        role="tablist"
        aria-label="Time off lists"
        className="flex border-b border-buttonBorder-tertiary"
      >
        {tabs.map((item) => {
          const selected = item.value === activeTab;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setTab(item.value)}
              className={cn(
                "-mb-px px-4 py-3 font-body text-body-3",
                selected
                  ? "border-b-2 border-text-primary text-text-primary"
                  : "text-text-secondary",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div
        className={cn(
          "flex flex-col",
          rows.length >= 3 && "max-h-[5.75rem] overflow-y-auto",
        )}
      >
        {rows.length === 0 && (
          <p className="py-4 font-body text-body-2 text-text-tertiary">
            No time off here.
          </p>
        )}
        {rows.map((entry) => (
          <TimeOffRow
            key={entry.id}
            entry={entry}
            zone={timezone}
            mode={activeTab}
            disabled={disabled}
            onEdit={() => {
              setEditing(entry);
              setOpen(true);
            }}
            onDelete={() => setConfirming({ id: entry.id, mode: "upcoming" })}
            onCancelCurrent={() =>
              setConfirming({ id: entry.id, mode: "current" })
            }
          />
        ))}
      </div>
      <ConfirmActionDialog
        isOpen={confirming !== null}
        loading={confirmingPending}
        title={
          confirming?.mode === "current"
            ? "End this time off?"
            : "Cancel this time off?"
        }
        description={
          confirming?.mode === "current"
            ? "This ends it now. The rest of the window moves to Past."
            : "This removes the planned time off. It has not started."
        }
        confirmText={
          confirming?.mode === "current" ? "End time off" : "Cancel time off"
        }
        onClose={() => {
          if (confirmingPending) return;
          setConfirming(null);
        }}
        onConfirm={() => {
          if (!confirming) return;
          const request =
            confirming.mode === "current" ? onCancelCurrent : onDelete;
          setConfirmingPending(true);
          Promise.resolve(request(confirming.id))
            .then(() => setConfirming(null))
            .catch(() => undefined)
            .finally(() => setConfirmingPending(false));
        }}
      />
    </section>
  );
}

function TimeOffRow({
  entry,
  zone,
  mode,
  disabled,
  onEdit,
  onDelete,
  onCancelCurrent,
}: {
  entry: TimeOffEntry;
  zone: string;
  mode: string;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCancelCurrent: () => void;
}) {
  const canceled = canceledLine(entry, zone);
  return (
    <div className="grid grid-cols-[5.25rem_minmax(0,1fr)_auto] items-start gap-x-4 border-t border-buttonBorder-tertiary py-3 first:border-t-0">
      <Badge className={cn("mt-0.5 w-fit", BADGE_CLASS[entry.type])}>
        {timeOffBadge(entry.type)}
      </Badge>
      <div className="min-w-0">
        <p className="font-body text-body-2 text-text-primary">
          {formatWindow(entry, zone)}
        </p>
        {canceled && (
          <>
            <p className="font-body text-body-3 text-text-tertiary">
              Ended early
            </p>
            <p className="font-body text-body-3 text-text-tertiary">
              {canceled}
            </p>
          </>
        )}
        {entry.note && (
          <p className="truncate font-body text-body-2 italic text-text-secondary">
            “{entry.note}”
          </p>
        )}
      </div>
      <div className="flex items-center justify-end gap-4">
        {!disabled && mode === "upcoming" && (
          <div className="flex gap-2">
            <Button
              fullWidth={false}
              variant="quaternary"
              size="sm"
              onPress={onEdit}
            >
              Edit
            </Button>
            <Button
              fullWidth={false}
              variant="quaternary"
              size="sm"
              onPress={onDelete}
            >
              Cancel
            </Button>
          </div>
        )}
        {!disabled && mode === "current" && (
          <Button
            fullWidth={false}
            variant="quaternary"
            size="sm"
            onPress={onCancelCurrent}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function TimeOffForm({
  timezone,
  editing,
  entries,
  pending,
  onCancel,
  onSave,
}: {
  timezone: string;
  editing: TimeOffEntry | null;
  entries: TimeOffEntry[];
  pending?: boolean;
  onCancel: () => void;
  onSave: (draft: TimeOffDraft) => Promise<void>;
}) {
  const [overlap, setOverlap] = useState<string | null>(null);
  const form = useForm<TimeOffFormValues>({
    resolver: zodResolver(timeOffSchema),
    defaultValues: editing
      ? valuesFromEntry(editing, timezone)
      : {
          type: "pto",
          note: "",
          ...defaultTimeOffDates(timezone),
          startTime: "00:00",
          endTime: "23:59",
        },
  });
  const values = form.watch();
  const draft: TimeOffDraft = {
    type: values.type,
    note: values.note,
    startDate: values.startDate,
    endDate: values.endDate,
    startTime: values.startTime,
    endTime: values.endTime,
  };
  const window = draftInstants(draft, timezone);
  const orderError =
    window && window.end <= window.start
      ? "The end has to be after the start."
      : null;

  const submit = form.handleSubmit(async (submitted) => {
    const next: TimeOffDraft = {
      type: submitted.type,
      note: submitted.note,
      startDate: submitted.startDate,
      endDate: submitted.endDate,
      startTime: submitted.startTime,
      endTime: submitted.endTime,
    };
    const nextWindow = draftInstants(next, timezone);
    if (nextWindow && nextWindow.end <= nextWindow.start) return;
    const conflict = findOverlap(next, entries, timezone, editing?.id);
    if (conflict) {
      setOverlap(overlapMessage(conflict));
      return;
    }
    setOverlap(null);
    await onSave(next);
  });

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-2xl bg-surfaces-z2 p-4"
    >
      <div>
        <p className="mb-2 font-body text-body-4 uppercase tracking-wide text-text-secondary">
          Type
        </p>
        <Controller
          control={form.control}
          name="type"
          render={({ field }) => (
            <div role="radiogroup" aria-label="Type" className="flex gap-3">
              {TYPE_OPTIONS.map((option) => {
                const selected = field.value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => field.onChange(option.value)}
                    className={cn(
                      "flex min-w-0 flex-1 items-center gap-3 rounded-3xl bg-surfaces-z0 px-6 py-4 text-left",
                      selected
                        ? "border-2 border-text-primary"
                        : "border border-buttonBorder-tertiary",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-body text-body-2 text-text-primary">
                        {option.title}
                      </span>
                      <span className="block font-body text-body-3 text-text-secondary">
                        {option.description}
                      </span>
                    </span>
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-icon-secondary">
                      {selected && (
                        <span className="size-2.5 rounded-full bg-brand-trueBlack" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        />
      </div>
      <Controller
        control={form.control}
        name="note"
        render={({ field }) => (
          <TextInput
            id="time-off-note"
            label="Note (optional)"
            labelClassName="font-body text-body-3 text-text-secondary"
            placeholder="Anything to know about this time off?"
            maxLength={50}
            value={field.value}
            onChange={field.onChange}
            helperText={`${field.value.length}/50`}
            helperTextClassName="font-body text-body-4 text-text-tertiary"
            inputClassName="h-14 rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 px-5 font-body text-body-2 text-text-primary focus:border-text-primary"
          />
        )}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          label="Start date"
          value={values.startDate}
          onChange={(value) => {
            form.setValue("startDate", value);
            if (values.endDate && value && values.endDate < value) {
              form.setValue("endDate", value);
            }
          }}
        />
        <DateField
          label="End date"
          value={values.endDate}
          minDate={values.startDate}
          onChange={(value) => form.setValue("endDate", value)}
        />
        <TimeField
          label={`Start time (${zoneAbbreviation(timezone)})`}
          value={values.startTime}
          options={withChoice(START_TIMES, values.startTime)}
          onChange={(value) => form.setValue("startTime", value)}
        />
        <TimeField
          label={`End time (${zoneAbbreviation(timezone)})`}
          value={values.endTime}
          options={withChoice(END_TIMES, values.endTime)}
          onChange={(value) => form.setValue("endTime", value)}
        />
      </div>
      <div className="flex gap-3 rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 p-4">
        <CalendarClock className="mt-0.5 size-4 shrink-0 text-icon-tertiary" />
        <div>
          <p className="font-body text-body-4 uppercase tracking-wide text-text-tertiary">
            Preview
          </p>
          <p className="mt-1 font-body text-body-2 text-text-primary">
            {previewCopy(draft, timezone)}
          </p>
        </div>
      </div>
      <p className="flex items-center gap-2 font-body text-body-3 text-text-secondary">
        <Info className="size-3.5 shrink-0 text-icon-tertiary" />
        Please schedule your time off in your current timezone,{" "}
        {zoneLabel(timezone)}.
      </p>
      {(overlap || orderError) && (
        <p className="font-body text-body-2 text-states-error">
          {overlap ?? orderError}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          fullWidth={false}
          variant="quaternary"
          size="sm"
          type="button"
          onPress={onCancel}
        >
          Cancel
        </Button>
        <Button
          fullWidth={false}
          variant="primary"
          size="sm"
          type="submit"
          loading={pending}
        >
          {editing ? "Save" : "Add time off"}
        </Button>
      </div>
    </form>
  );
}

function DateField({
  label,
  value,
  minDate,
  onChange,
}: {
  label: string;
  value: string;
  minDate?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ignoreOpen = useRef(false);
  const shown = value
    ? DateTime.fromISO(value).toFormat("MM/dd/yyyy")
    : "MM/DD/YYYY";

  return (
    <div className="flex flex-col gap-1">
      <span className="font-body text-body-3 text-text-secondary">{label}</span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next && ignoreOpen.current) return;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <button type="button" className={FIELD_CLASS}>
            <span className={value ? undefined : "text-text-tertiary"}>
              {shown}
            </span>
            <Calendar className="size-4 text-icon-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <CalendarSmall
            mode="single"
            defaultMonth={
              value
                ? pickerDate(value)
                : minDate
                  ? pickerDate(minDate)
                  : DateTime.now().toJSDate()
            }
            selected={value ? pickerDate(value) : undefined}
            disabled={minDate ? { before: pickerDate(minDate) } : undefined}
            classNames={{
              months: "relative flex flex-col",
              month_caption: "flex h-9 items-center justify-center",
              nav: "absolute inset-x-0 top-0 z-10 flex h-9 items-center justify-between px-1",
              button_previous:
                "inline-flex size-7 items-center justify-center rounded-md text-text-primary",
              button_next:
                "inline-flex size-7 items-center justify-center rounded-md text-text-primary",
            }}
            onSelect={(date) => {
              if (!date) return;
              onChange(isoFromPicker(date));
              // Closing immediately unmounts the calendar under the pointer, and that same click reopens it.
              ignoreOpen.current = true;
              window.setTimeout(() => {
                setOpen(false);
                ignoreOpen.current = false;
              }, 200);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TimeField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-body text-body-3 text-text-secondary">{label}</span>
      <Selector
        aria-label={label}
        options={options}
        value={value}
        onChange={onChange}
        triggerClassName={FIELD_CLASS}
        contentClassName={TIME_MENU_CLASS}
        onOpenChange={scrollSelectedTime}
      />
    </div>
  );
}

function withChoice(
  options: { value: string; label: string }[],
  value: string,
) {
  if (options.some((option) => option.value === value)) return options;
  return [{ value, label: value }, ...options];
}

function valuesFromEntry(entry: TimeOffEntry, zone: string): TimeOffFormValues {
  const start = DateTime.fromISO(entry.startsAt, { setZone: true }).setZone(
    zone,
  );
  const end = displayEnd(entry.endsAt, zone);
  return {
    type: entry.type,
    note: entry.note ?? "",
    startDate: start.toISODate() ?? "",
    endDate: end.toISODate() ?? "",
    startTime: start.toFormat("HH:mm"),
    endTime: end.toFormat("HH:mm"),
  };
}
